import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { FadeLoader } from "react-spinners";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import DataGridTable from "../table/DataGridTable";
import translations from "../../assets/locals/translations";
import "../../styles/TargetReport.css";
import "../../styles/TaskAnalyticsReport.css";

const TargetReport = () => {
  const { view = "monthly" } = useParams();
  const navigate = useNavigate();
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations["en"];

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [weekIndex, setWeekIndex] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState([]);
  const [meta, setMeta] = useState({});
  const [error, setError] = useState(null);
  const [employeeFilter, setEmployeeFilter] = useState("");

  const baseUrl = `${process.env.REACT_APP_NODE_URL}/api/targets`;
  const config = {
    headers: { token: localStorage.getItem("rbacToken") },
  };

  const period = view === "week" ? "week" : view === "annual" ? "annual" : "month";

  useEffect(() => {
    let isMounted = true;

    async function fetchReport() {
      setLoading(true);
      setError(null);
      try {
        let url = `${baseUrl}/report?period=${period}&year=${year}`;
        if (period !== "annual") url += `&month=${month}`;
        if (period === "week") url += `&weekIndex=${weekIndex}`;

        const res = await axios.get(url, config);
        if (!isMounted) return;
        if (res.data?.isSuccess) {
          setReportData(res.data.result || []);
          setMeta(res.data.meta || {});
        } else {
          setError(res.data?.result || "Failed to load report");
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.response?.data?.result || err?.message || "Failed to load target report");
          setReportData([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchReport();
    return () => { isMounted = false; };
  }, [period, year, month, weekIndex]);

  const viewLabels = {
    week: t.weeklytargets || "Weekly Targets",
    monthly: t.monthlytargets || "Monthly Targets",
    annual: t.annualtargets || "Annual Targets",
  };

  const monthName = (m) => new Date(2000, m - 1).toLocaleString(currentLanguage || "en", { month: "long" });

  const columns = [
    { field: "indexID", headerName: t.id || "ID", width: 70 },
    {
      field: "employee_name",
      headerAlign: "left",
      align: "left",
      headerName: t.employeeName || "Employee Name",
      minWidth: 160,
      flex: 1,
    },
    {
      field: "name",
      headerAlign: "left",
      align: "left",
      headerName: t.name || "Name",
      minWidth: 160,
      flex: 1,
    },
    {
      field: "type",
      headerAlign: "left",
      align: "left",
      headerName: "Type",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "target",
      headerAlign: "left",
      align: "left",
      headerName: "Target",
      minWidth: 90,
      flex: 1,
    },
    {
      field: "completed",
      headerAlign: "left",
      align: "left",
      headerName: "Completed",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "remaining",
      headerAlign: "left",
      align: "left",
      headerName: "Remaining",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "pct",
      headerAlign: "left",
      align: "left",
      headerName: "%",
      minWidth: 70,
      flex: 1,
    },
  ];

  const tableRows = useMemo(() => {
    const rows = [];
    let indexID = 0;
    reportData.forEach((emp) => {
      if (emp.details && emp.details.length > 0) {
        emp.details.forEach((row) => {
          const pct = row.target > 0 ? Math.min(100, Math.round((row.completed / row.target) * 100)) : 0;
          rows.push({
            indexID: ++indexID,
            userId: emp.userId,
            employee_name: emp.userName,
            name: row.name,
            type: row.type,
            target: row.target,
            completed: row.completed,
            remaining: row.remaining,
            pct: `${pct}%`,
          });
        });
      } else {
        rows.push({
          indexID: ++indexID,
          userId: emp.userId,
          employee_name: emp.userName,
          name: "-",
          type: "-",
          target: emp.totalTarget ?? 0,
          completed: emp.totalCompleted ?? 0,
          remaining: emp.totalRemaining ?? 0,
          pct: "",
        });
      }
    });
    return rows;
  }, [reportData]);

  const employeeOptions = useMemo(() => {
    const opts = [{ value: "-1", name: t.all || "All" }];
    reportData.forEach((emp) => {
      opts.push({ value: String(emp.userId), name: emp.userName });
    });
    return opts;
  }, [reportData, t.all]);

  const filteredData = useMemo(() => {
    if (!employeeFilter || employeeFilter === "-1") return tableRows;
    return tableRows.filter((row) => String(row.userId) === employeeFilter);
  }, [tableRows, employeeFilter]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === "employee") setEmployeeFilter(value);
  };

  const handleDownloadPdf = () => {
    if (!filteredData.length) return;

    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = 36;
    const marginTop = 40;
    const usableWidth = pageWidth - marginX * 2;

    const periodLabel =
      period === "week"
        ? `Week ${weekIndex} - ${monthName(month)} ${year}`
        : period === "annual"
        ? `Annual Targets ${year}`
        : `Monthly Targets ${monthName(month)} ${year}`;

    doc.setFontSize(14);
    doc.text(t.targetreport || "Target Report", marginX, marginTop);
    doc.setFontSize(10);
    doc.text(periodLabel, marginX, marginTop + 16);

    const headers = [t.employeeName || "Employee Name", t.name || "Name", "Type", "Target", "Completed", "Remaining", "%"];
    const bodyRows = filteredData.map((row) => [
      row.employee_name,
      row.name,
      row.type,
      String(row.target ?? 0),
      String(row.completed ?? 0),
      String(row.remaining ?? 0),
      row.pct || "",
    ]);

    autoTable(doc, {
      startY: marginTop + 32,
      head: [headers],
      body: bodyRows,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: 20,
        halign: "left",
      },
      columnStyles: {
        0: { cellWidth: usableWidth * 0.22 },
        1: { cellWidth: usableWidth * 0.22 },
        2: { cellWidth: usableWidth * 0.12 },
        3: { cellWidth: usableWidth * 0.11 },
        4: { cellWidth: usableWidth * 0.11 },
        5: { cellWidth: usableWidth * 0.11 },
        6: { cellWidth: usableWidth * 0.06 },
      },
    });

    const fileSuffix =
      period === "week"
        ? `Week_${weekIndex}_${year}_${month}`
        : period === "annual"
        ? `Annual_${year}`
        : `Month_${year}_${month}`;
    doc.save(`Target_Report_${fileSuffix}.pdf`);
  };

  const handleDownloadExcel = () => {
    if (!filteredData.length) return;

    const periodDescription =
      period === "week"
        ? `Weekly Targets - ${monthName(month)} ${year}, Week ${weekIndex}`
        : period === "annual"
        ? `Annual Targets - ${year}`
        : `Monthly Targets - ${monthName(month)} ${year}`;

    const periodLabel = period === "week" ? `Week_${weekIndex}_${year}_${month}` : period === "annual" ? `Annual_${year}` : `Month_${year}_${month}`;
    const headers = [t.employeeName || "Employee Name", t.name || "Name", "Type", "Target", "Completed", "Remaining", "%"];

    const rows = [];
    rows.push([t.targetreport || "Target Report"]);
    rows.push(["Period:", periodDescription]);
    rows.push(["Generated:", new Date().toLocaleString()]);
    rows.push([]);
    rows.push(headers);
    filteredData.forEach((row) => {
      rows.push([row.employee_name, row.name, row.type, row.target, row.completed, row.remaining, row.pct || ""]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 22 }, { wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 8 }];
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
      { s: { r: 1, c: 1 }, e: { r: 1, c: 6 } },
      { s: { r: 2, c: 1 }, e: { r: 2, c: 6 } },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Target Report");
    XLSX.writeFile(wb, `Target_Report_${periodLabel}.xlsx`);
  };

  return (
    <div className="bg-white">
      <div className="target-report-header" style={{ marginBottom: 16 }}>
        <h2 className="target-report-title">{t.targetreport || "Target Report"}</h2>
        <div className="target-report-view-tabs">
          {["week", "monthly", "annual"].map((v) => (
            <button
              key={v}
              type="button"
              className={`target-view-tab ${v === view ? "active" : ""}`}
              onClick={() => navigate(`/reports/targets/${v}`)}
            >
              {viewLabels[v]}
            </button>
          ))}
        </div>
      </div>

      <div className="filter-card">
        <form
          onSubmit={(e) => e.preventDefault()}
          className="filter-form"
        >
          <div className="filter-group">
            <label htmlFor="employee">{t.employee || "Employee"}</label>
            <select
              name="employee"
              id="employee"
              value={employeeFilter}
              onChange={handleFilterChange}
            >
              <option value="">{t.selectEmployee || "Select Employee"}</option>
              {employeeOptions.map((emp) => (
                <option key={emp.value} value={emp.value}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>{t.year || "Year"}</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="target-report-select"
            >
              {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          {period !== "annual" && (
            <div className="filter-group">
              <label>{t.month || "Month"}</label>
              <select
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="target-report-select"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                  <option key={m} value={m}>{monthName(m)}</option>
                ))}
              </select>
            </div>
          )}
          {period === "week" && (
            <div className="filter-group">
              <label>{t.week || "Week"}</label>
              <select
                value={weekIndex}
                onChange={(e) => setWeekIndex(Number(e.target.value))}
                className="target-report-select"
              >
                {[1, 2, 3, 4, 5].map((w) => (
                  <option key={w} value={w}>Week {w}</option>
                ))}
              </select>
            </div>
          )}
          <div className="button-group">
            <button type="button" className="filter-button" onClick={handleDownloadPdf} disabled={filteredData.length === 0}>
              <FaFilePdf style={{ marginRight: "8px" }} />
              {t.downloadPdf || "Download PDF"}
            </button>
            <button type="button" className="filter-button excel-btn" onClick={handleDownloadExcel} disabled={filteredData.length === 0}>
              <FaFileExcel style={{ marginRight: "8px" }} />
              {t.downloadExcel || "Download Excel"}
            </button>
          </div>
        </form>
      </div>

      {error && <div className="target-report-error">{error}</div>}

      {loading ? (
        <div className="target-report-loading">
          <FadeLoader size={10} color="#808b96" />
          <span>{t.loadingPleaseWait || "Loading, please wait..."}</span>
        </div>
      ) : reportData.length === 0 ? (
        <div className="target-report-empty">
          {t.noDataAvailable || "No target data available."}
        </div>
      ) : (
        <DataGridTable
          data={filteredData}
          columns={columns}
          storageKey="target_report"
        />
      )}
    </div>
  );
};

export default TargetReport;
